import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Hero3D from "../components/Hero3D";
import PricingCard from "../components/PricingCard";
import { Server, Cloud, Globe, Shield, Mail, Zap, ArrowRight, Star, CheckCircle, Award, Users, Cpu, HardDrive } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const services = [
  { icon: Server, label: "VPS Hosting", desc: "Full root access. Dedicated resources.", href: "/vps-hosting", color: "#00F3FF", from: "₹599/mo" },
  { icon: Cloud, label: "Cloud Hosting", desc: "20x power. Managed & scalable.", href: "/cloud-hosting", color: "#BC13FE", from: "₹549/mo" },
  { icon: Globe, label: "Shared Hosting", desc: "Perfect for beginners & blogs.", href: "/shared-hosting", color: "#00FF94", from: "₹69/mo" },
  { icon: Cpu, label: "GPU Cloud", desc: "NVIDIA A100, H200 & RTX Blackwell.", href: "/gpu-servers", color: "#BC13FE", from: "₹3,999/mo" },
  { icon: Globe, label: "Domain Names", desc: ".in, .com, .ai & 50+ more.", href: "/domains", color: "#00F3FF", from: "₹299/yr" },
  { icon: Shield, label: "SSL Certificates", desc: "Secure your website today.", href: "/ssl", color: "#BC13FE", from: "₹199/yr" },
  { icon: Mail, label: "Email Hosting", desc: "Professional business email.", href: "/email-hosting", color: "#00FF94", from: "₹79/mo" },
];

const features = [
  { icon: Cpu, title: "AMD EPYC Processors", desc: "Latest gen CPUs for blazing performance" },
  { icon: HardDrive, title: "NVMe SSD Storage", desc: "10x faster than traditional SSDs" },
  { icon: Shield, title: "DDoS Protection", desc: "Enterprise-grade Wanguard filtering" },
  { icon: Zap, title: "99.9% Uptime SLA", desc: "Guaranteed reliability for your business" },
  { icon: Globe, title: "10+ Datacenters", desc: "India + global network for speed" },
  { icon: CheckCircle, title: "24/7 AI Support", desc: "CloudBot answers in under 2 minutes" },
];

const testimonials = [
  { name: "Rahul Sharma", role: "Founder, TechStartup.in", text: "IndiaCloud's VPS performance is unmatched. Our app loads 3x faster now and the pricing is incredible for what you get.", rating: 5 },
  { name: "Priya Mehta", role: "WordPress Developer", text: "The managed WordPress hosting is a game-changer. Auto-updates, AI troubleshooter, and the support team is super responsive.", rating: 5 },
  { name: "Arjun Nair", role: "E-commerce Owner", text: "Migrated from a US host and the India datacenter makes a huge difference. Zero downtime, faster speeds, and ₹ pricing is perfect.", rating: 5 },
];

function CounterStat({ value, label }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const numeric = parseInt(value.replace(/[^0-9]/g, ""));
        const duration = 1800;
        const step = Math.ceil(numeric / (duration / 16));
        let current = 0;
        const timer = setInterval(() => {
          current += step;
          if (current >= numeric) { setCount(numeric); clearInterval(timer); }
          else setCount(current);
        }, 16);
      }
    });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value]);

  return (
    <div ref={ref} className="text-center">
      <div className="font-orbitron font-900 text-3xl lg:text-4xl text-gradient mb-1">
        {value.includes("K") ? `${count}K+` : value.includes("%") ? `${count}%` : `${count}+`}
      </div>
      <div className="font-outfit text-sm" style={{color:"#e5e7eb"}}>{label}</div>
    </div>
  );
}

export default function Home() {
  const [popularPlans, setPopularPlans] = useState([]);
  const [billingPeriod, setBillingPeriod] = useState("monthly");

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const [vps, shared] = await Promise.all([
          axios.get(`${API}/plans/category/vps`),
          axios.get(`${API}/plans/category/shared`),
        ]);
        const combined = [
          ...vps.data.filter((p) => p.highlighted),
          ...shared.data.filter((p) => p.highlighted),
        ].slice(0, 3);
        setPopularPlans(combined);
      } catch (e) {
        console.error(e);
      }
    };
    fetchPlans();
  }, []);

  return (
    <div data-testid="home-page" className="page-enter">
      {/* Hero */}
      <Hero3D />

      {/* Stats */}
      <section className="py-16 border-y" style={{ borderColor: "rgba(0,243,255,0.06)", background: "rgba(10,10,18,0.5)" }}>
        <div className="max-w-5xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
          <CounterStat value="50K" label="Active Customers" />
          <CounterStat value="99.9%" label="Uptime Guarantee" />
          <CounterStat value="10" label="Global Datacenters" />
          <CounterStat value="4" label="Min Support Response" />
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 section-bg">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 rounded-full text-xs font-rajdhani font-700 tracking-widest"
              style={{ background: "rgba(188,19,254,0.08)", border: "1px solid rgba(188,19,254,0.2)", color: "#BC13FE" }}>
              HOSTING SOLUTIONS
            </div>
            <h2 className="font-orbitron font-700 text-3xl lg:text-4xl text-white mb-4">
              Everything You Need to
              <span className="text-gradient"> Launch & Scale</span>
            </h2>
            <p className="font-outfit text-base max-w-2xl mx-auto" style={{color:"#e5e7eb"}} style={{ fontWeight: 300 }}>
              From personal blogs to enterprise applications — IndiaCloud has the perfect hosting solution for every need.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {services.map((svc) => (
              <Link
                key={svc.href}
                to={svc.href}
                data-testid={`service-card-${svc.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="no-underline group"
              >
                <div
                  className="glass rounded-2xl p-5 flex flex-col gap-3 transition-all duration-300 group-hover:-translate-y-2 cursor-pointer h-full"
                  style={{ border: `1px solid rgba(${svc.color === "#00F3FF" ? "0,243,255" : svc.color === "#BC13FE" ? "188,19,254" : svc.color === "#00FF94" ? "0,255,148" : "255,204,0"},0.12)` }}
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ background: `${svc.color}15`, border: `1px solid ${svc.color}30` }}
                  >
                    <svc.icon size={18} color={svc.color} />
                  </div>
                  <div>
                    <h3 className="font-rajdhani font-700 text-sm text-white mb-1">{svc.label}</h3>
                    <p className="font-outfit text-xs" style={{color:"#d1d5db"}} style={{ fontWeight: 300 }}>{svc.desc}</p>
                  </div>
                  <div className="mt-auto font-orbitron text-xs" style={{ color: svc.color }}>
                    From {svc.from}
                  </div>
                </div>
              </Link>
            ))}

            {/* AI Recommender CTA */}
            <Link to="/plan-recommender" className="no-underline group">
              <div
                className="rounded-2xl p-5 flex flex-col gap-3 transition-all duration-300 group-hover:-translate-y-2 cursor-pointer h-full"
                style={{
                  background: "linear-gradient(135deg, rgba(0,243,255,0.08), rgba(188,19,254,0.08))",
                  border: "1px solid rgba(0,243,255,0.2)",
                }}
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: "linear-gradient(135deg, rgba(0,243,255,0.2), rgba(188,19,254,0.2))", border: "1px solid rgba(0,243,255,0.3)" }}>
                  <Zap size={18} color="#00F3FF" />
                </div>
                <div>
                  <h3 className="font-rajdhani font-700 text-sm text-white mb-1">AI Plan Picker</h3>
                  <p className="font-outfit text-xs" style={{color:"#d1d5db"}} style={{ fontWeight: 300 }}>Not sure? Let AI choose for you.</p>
                </div>
                <div className="mt-auto font-rajdhani text-xs font-700 flex items-center gap-1" style={{ color: "#00F3FF" }}>
                  TRY IT FREE <ArrowRight size={10} />
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Popular Plans */}
      {popularPlans.length > 0 && (
        <section data-testid="popular-plans-section" className="py-20" style={{ background: "rgba(10,10,18,0.3)" }}>
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-14">
              <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 rounded-full text-xs font-rajdhani font-700 tracking-widest"
                style={{ background: "rgba(0,255,148,0.08)", border: "1px solid rgba(0,255,148,0.2)", color: "#00FF94" }}>
                <Award size={12} /> MOST POPULAR PLANS
              </div>
              <h2 className="font-orbitron font-700 text-3xl lg:text-4xl text-white mb-4">
                Start Fast. <span className="text-gradient">Scale Instantly.</span>
              </h2>

              {/* Billing Toggle */}
              <div data-testid="home-billing-toggle" className="inline-flex items-center gap-1 p-1 rounded-full mt-4"
                style={{ background: "rgba(10,10,18,0.9)", border: "1px solid rgba(0,243,255,0.15)" }}>
                <button
                  data-testid="home-billing-monthly"
                  onClick={() => setBillingPeriod("monthly")}
                  className="px-5 py-2 rounded-full text-xs font-rajdhani font-700 tracking-wider transition-all duration-300"
                  style={{
                    background: billingPeriod === "monthly" ? "linear-gradient(135deg, #00F3FF, #BC13FE)" : "transparent",
                    color: billingPeriod === "monthly" ? "#030305" : "#d1d5db",
                  }}
                >
                  MONTHLY
                </button>
                <button
                  data-testid="home-billing-yearly"
                  onClick={() => setBillingPeriod("yearly")}
                  className="px-5 py-2 rounded-full text-xs font-rajdhani font-700 tracking-wider transition-all duration-300 flex items-center gap-1.5"
                  style={{
                    background: billingPeriod === "yearly" ? "linear-gradient(135deg, #00F3FF, #BC13FE)" : "transparent",
                    color: billingPeriod === "yearly" ? "#030305" : "#d1d5db",
                  }}
                >
                  YEARLY
                  <span className="text-xs px-1.5 py-0.5 rounded-md font-600"
                    style={{
                      background: billingPeriod === "yearly" ? "rgba(0,0,0,0.2)" : "rgba(0,255,148,0.15)",
                      color: billingPeriod === "yearly" ? "#030305" : "#00FF94",
                      fontSize: "9px",
                    }}>
                    -20%
                  </span>
                </button>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {popularPlans.map((plan) => (
                <PricingCard key={plan.id} plan={plan} billingPeriod={billingPeriod} />
              ))}
            </div>
            <div className="text-center mt-10">
              <Link to="/vps-hosting" className="no-underline">
                <button className="btn-neon px-8 py-3 text-sm flex items-center gap-2 mx-auto">
                  VIEW ALL PLANS <ArrowRight size={14} />
                </button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Features */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="font-orbitron font-700 text-3xl lg:text-4xl text-white mb-4">
              Why Choose <span className="text-gradient">IndiaCloud?</span>
            </h2>
            <p className="font-outfit text-base max-w-xl mx-auto" style={{color:"#e5e7eb"}} style={{ fontWeight: 300 }}>
              Built on cutting-edge infrastructure with India-first values.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feat) => (
              <div key={feat.title} className="glass rounded-2xl p-6 hover:-translate-y-1 transition-all duration-300">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: "rgba(0,243,255,0.1)", border: "1px solid rgba(0,243,255,0.2)" }}>
                  <feat.icon size={20} color="#00F3FF" />
                </div>
                <h3 className="font-rajdhani font-700 text-base text-white mb-2">{feat.title}</h3>
                <p className="font-outfit text-sm" style={{color:"#e5e7eb"}} style={{ fontWeight: 300 }}>{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20" style={{ background: "rgba(10,10,18,0.4)" }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 rounded-full text-xs font-rajdhani font-700 tracking-widest"
              style={{ background: "rgba(0,243,255,0.08)", border: "1px solid rgba(0,243,255,0.2)", color: "#00F3FF" }}>
              <Users size={12} /> TRUSTED BY THOUSANDS
            </div>
            <h2 className="font-orbitron font-700 text-3xl text-white">
              What Our <span className="text-gradient">Customers Say</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div key={t.name} className="testimonial-card">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(t.rating)].map((_, i) => (
                    <Star key={i} size={13} color="#ffcc00" fill="#ffcc00" />
                  ))}
                </div>
                <p className="font-outfit text-sm text-gray-400 leading-relaxed mb-4" style={{ fontWeight: 300 }}>
                  "{t.text}"
                </p>
                <div>
                  <div className="font-rajdhani font-700 text-sm text-white">{t.name}</div>
                  <div className="font-outfit text-xs" style={{color:"#d1d5db"}}>{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div
            className="rounded-3xl p-12 relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, rgba(0,243,255,0.08), rgba(188,19,254,0.08))",
              border: "1px solid rgba(0,243,255,0.2)",
            }}
          >
            <div className="absolute inset-0 pointer-events-none"
              style={{ background: "radial-gradient(circle at 50% 0%, rgba(0,243,255,0.1) 0%, transparent 60%)" }} />
            <h2 className="font-orbitron font-900 text-3xl lg:text-4xl text-white mb-4 relative z-10">
              Ready to <span className="text-gradient">Launch?</span>
            </h2>
            <p className="font-outfit text-base text-gray-400 mb-8 max-w-xl mx-auto relative z-10" style={{ fontWeight: 300 }}>
              Join 50,000+ Indian businesses. Get started with a plan that scales with you.
              30-day money-back guarantee.
            </p>
            <div className="flex items-center justify-center gap-4 flex-wrap relative z-10">
              <Link to="/plan-recommender" className="no-underline">
                <button className="btn-primary flex items-center gap-2 px-8 py-3 text-sm font-700">
                  <Zap size={16} /> LET AI PICK MY PLAN
                </button>
              </Link>
              <Link to="/shared-hosting" className="no-underline">
                <button className="btn-neon flex items-center gap-2 px-8 py-3 text-sm">
                  VIEW ALL PLANS <ArrowRight size={14} />
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
